import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { P1S } from '../settings/schema';

function themeIsDark() {
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

import { store, useAppStore, isOutOfBounds } from '../store/appStore';
import type { PlacedObject } from '../store/types';

function makePeiTexture(dark: boolean): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 512;
  const ctx = c.getContext('2d')!;
  const g = ctx.createLinearGradient(0, 0, 512, 512);
  if (dark) {
    g.addColorStop(0, '#3a3228');
    g.addColorStop(0.5, '#4a4034');
    g.addColorStop(1, '#322b22');
  } else {
    // Light plate #E8E8E8 so green/teal models stand out
    g.addColorStop(0, '#f3f3f3');
    g.addColorStop(0.5, '#e8e8e8');
    g.addColorStop(1, '#ececec');
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 512, 512);
  for (let i = 0; i < 9000; i++) {
    const x = Math.random() * 512;
    const y = Math.random() * 512;
    const a = dark ? 0.04 + Math.random() * 0.07 : 0.02 + Math.random() * 0.04;
    if (dark) {
      ctx.fillStyle = `rgba(${180 + Math.random() * 40},${150 + Math.random() * 30},90,${a})`;
    } else {
      const v = 160 + Math.random() * 50;
      ctx.fillStyle = `rgba(${v},${v},${v},${a})`;
    }
    ctx.fillRect(x, y, 1.5, 1.5);
  }
  ctx.strokeStyle = dark ? 'rgba(200,160,80,0.1)' : 'rgba(120,120,120,0.12)';
  ctx.lineWidth = 1;
  for (let y = 0; y < 512; y += 16) {
    for (let x = 0; x < 512; x += 14) {
      ctx.strokeRect(x + (y % 32 === 0 ? 0 : 7), y, 12, 14);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function meshFromObject(o: PlacedObject, color: number, paint?: boolean): THREE.Mesh {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(o.positions, 3));
  geo.computeVertexNormals();

  let mat: THREE.Material;
  if (paint && o.paintSupports) {
    // Vertex colors from triangle paint
    const colors = new Float32Array(o.positions.length);
    const base = new THREE.Color(color);
    const enforce = new THREE.Color(0x22c55e);
    const block = new THREE.Color(0xef4444);
    for (let t = 0; t < o.paintSupports.length; t++) {
      const v = o.paintSupports[t];
      const c = v === 1 ? enforce : v === 2 ? block : base;
      for (let k = 0; k < 3; k++) {
        const i = (t * 3 + k) * 3;
        colors[i] = c.r;
        colors[i + 1] = c.g;
        colors[i + 2] = c.b;
      }
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      metalness: 0.05,
      roughness: 0.65,
      side: THREE.DoubleSide,
    });
  } else {
    mat = new THREE.MeshStandardMaterial({
      color,
      metalness: 0.08,
      roughness: 0.55,
      side: THREE.DoubleSide,
    });
  }

  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.set(o.position[0], o.position[2], o.position[1]);
  mesh.rotation.set(o.rotation[0], o.rotation[1], o.rotation[2]);
  mesh.scale.set(o.scale[0], o.scale[1], o.scale[2]);
  mesh.userData.objectId = o.id;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function applyOverhangColors(mesh: THREE.Mesh, angleDeg: number) {
  const geo = mesh.geometry as THREE.BufferGeometry;
  const pos = geo.getAttribute('position') as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const thresh = Math.cos((angleDeg * Math.PI) / 180);
  const up = new THREE.Vector3(0, 1, 0);
  const a = new THREE.Vector3(),
    b = new THREE.Vector3(),
    c = new THREE.Vector3(),
    n = new THREE.Vector3();
  const ok = new THREE.Color(0x60a5fa);
  const bad = new THREE.Color(0xf97316);
  for (let t = 0; t < pos.count; t += 3) {
    a.fromBufferAttribute(pos, t);
    b.fromBufferAttribute(pos, t + 1);
    c.fromBufferAttribute(pos, t + 2);
    n.crossVectors(b.clone().sub(a), c.clone().sub(a)).normalize();
    // Transform normal by mesh world (approx rotation only)
    n.applyEuler(mesh.rotation);
    const overhang = n.dot(up) < -thresh; // facing down past threshold
    const col = overhang ? bad : ok;
    for (let k = 0; k < 3; k++) {
      colors[(t + k) * 3] = col.r;
      colors[(t + k) * 3 + 1] = col.g;
      colors[(t + k) * 3 + 2] = col.b;
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  (mesh.material as THREE.MeshStandardMaterial).vertexColors = true;
  (mesh.material as THREE.MeshStandardMaterial).needsUpdate = true;
}

export interface PlateViewerHandle {
  captureThumbnail: () => Promise<Uint8Array | undefined>;
}

export function PlateViewer() {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const gridRef = useRef<THREE.GridHelper | null>(null);
  const volRef = useRef<THREE.LineSegments | null>(null);
  const plateMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const apiRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls: OrbitControls;
    objectGroup: THREE.Group;
    rebuild: () => void;
  } | null>(null);

  const objects = useAppStore((s) => s.objects);
  const selectedId = useAppStore((s) => s.selectedId);
  const theme = useAppStore((s) => s.theme);
  const tool = useAppStore((s) => s.tool);
  const brushSize = useAppStore((s) => s.brushSize);
  const overhangHighlight = useAppStore((s) => s.overhangHighlight);
  const overhangAngle = useAppStore((s) => s.overhangAngle);

  // Init three.js once
  useEffect(() => {
    const el = mountRef.current!;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(themeIsDark() ? 0x0f1218 : 0xf0f2f4);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 2000);
    camera.position.set(P1S.bedX * 0.9, P1S.bedZ * 0.7, P1S.bedY * 1.1);
    camera.lookAt(P1S.bedX / 2, 0, P1S.bedY / 2);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(P1S.bedX / 2, 20, P1S.bedY / 2);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    // Apple Pencil / stylus: treat as mouse
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN,
    };

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, themeIsDark() ? 0.5 : 0.75));
    const key = new THREE.DirectionalLight(0xfff2e0, 1.05);
    key.position.set(180, 320, 120);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0xa0c4ff, 0.35);
    fill.position.set(-120, 80, -80);
    scene.add(fill);

    // Build plate
    const plateGeo = new THREE.PlaneGeometry(P1S.bedX, P1S.bedY);
    const plateMat = new THREE.MeshStandardMaterial({
      map: makePeiTexture(themeIsDark()),
      color: 0xffffff,
      metalness: themeIsDark() ? 0.18 : 0.02,
      roughness: themeIsDark() ? 0.85 : 0.95,
      emissive: themeIsDark() ? 0x000000 : 0xb0b0b0,
      emissiveIntensity: themeIsDark() ? 0 : 0.35,
    });
    plateMatRef.current = plateMat;
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.rotation.x = -Math.PI / 2;
    plate.position.set(P1S.bedX / 2, 0, P1S.bedY / 2);
    plate.receiveShadow = true;
    scene.add(plate);

    // Grid — darker lines on light plate so model pops
    const grid = new THREE.GridHelper(
      P1S.bedX,
      16,
      themeIsDark() ? 0x6b5a3e : 0x52525b,
      themeIsDark() ? 0x3a3228 : 0x71717a
    );
    grid.position.set(P1S.bedX / 2, 0.05, P1S.bedY / 2);
    gridRef.current = grid;
    scene.add(grid);

    // Volume wireframe
    const vol = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.BoxGeometry(P1S.bedX, P1S.bedZ, P1S.bedY)),
      new THREE.LineBasicMaterial({ color: themeIsDark() ? 0x334155 : 0x4b5563, transparent: true, opacity: 0.55 })
    );
    vol.position.set(P1S.bedX / 2, P1S.bedZ / 2, P1S.bedY / 2);
    volRef.current = vol;
    scene.add(vol);

    const objectGroup = new THREE.Group();
    scene.add(objectGroup);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    function resize() {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    window.addEventListener('resize', resize);

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      controls.update();
      renderer.render(scene, camera);
    };
    tick();

    function rebuild() {
      const s = store.getSnapshot();
      while (objectGroup.children.length) {
        const ch = objectGroup.children.pop()!;
        (ch as THREE.Mesh).geometry?.dispose?.();
        const m = (ch as THREE.Mesh).material;
        if (Array.isArray(m)) m.forEach((x) => x.dispose());
        else m?.dispose?.();
      }
      for (const o of s.objects) {
        const selected = o.id === s.selectedId;
        const oob = isOutOfBounds(o);
        const color = oob ? 0xef4444 : selected ? 0x00AE42 : 0x8b95a1;
        const showPaint =
          s.tool.startsWith('paint') ||
          (o.paintSupports?.some((v) => v > 0) ?? false);
        const mesh = meshFromObject(o, color, showPaint && !s.overhangHighlight);
        if (s.overhangHighlight) applyOverhangColors(mesh, s.overhangAngle);
        if (selected) {
          const box = new THREE.BoxHelper(mesh, themeIsDark() ? 0xfbbf24 : 0x1f2937);
          objectGroup.add(box);
        }
        objectGroup.add(mesh);
      }
    }

    function ndcFromEvent(e: PointerEvent) {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    }

    function paintAt(e: PointerEvent) {
      const s = store.getSnapshot();
      if (!s.tool.startsWith('paint') || !s.selectedId) return;
      ndcFromEvent(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      ) as THREE.Mesh[];
      const hits = raycaster.intersectObjects(meshes, false);
      if (!hits.length) return;
      const hit = hits[0];
      const faceIndex = hit.faceIndex ?? 0;
      const mesh = hit.object as THREE.Mesh;
      // Expand brush by nearby triangle centroids
      const geo = mesh.geometry as THREE.BufferGeometry;
      const pos = geo.getAttribute('position') as THREE.BufferAttribute;
      const center = new THREE.Vector3();
      center.fromBufferAttribute(pos, faceIndex * 3);
      center.add(new THREE.Vector3().fromBufferAttribute(pos, faceIndex * 3 + 1));
      center.add(new THREE.Vector3().fromBufferAttribute(pos, faceIndex * 3 + 2));
      center.multiplyScalar(1 / 3);
      mesh.localToWorld(center);

      const radius = s.brushSize;
      const tris: number[] = [];
      const tmp = new THREE.Vector3();
      const triCount = pos.count / 3;
      for (let t = 0; t < triCount; t++) {
        tmp.fromBufferAttribute(pos, t * 3);
        tmp.add(new THREE.Vector3().fromBufferAttribute(pos, t * 3 + 1));
        tmp.add(new THREE.Vector3().fromBufferAttribute(pos, t * 3 + 2));
        tmp.multiplyScalar(1 / 3);
        mesh.localToWorld(tmp);
        if (tmp.distanceTo(center) <= radius) tris.push(t);
      }
      const value: 0 | 1 | 2 =
        s.tool === 'paint-enforce' ? 1 : s.tool === 'paint-block' ? 2 : 0;
      store.paintTriangles(s.selectedId, tris, value);
    }

    let painting = false;
    const onPointerDown = (e: PointerEvent) => {
      const s = store.getSnapshot();
      ndcFromEvent(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter((c) => (c as THREE.Mesh).isMesh) as THREE.Mesh[];
      const hits = raycaster.intersectObjects(meshes, false);

      if (s.tool.startsWith('paint')) {
        if (hits[0]?.object.userData.objectId) {
          store.select(hits[0].object.userData.objectId);
        }
        painting = true;
        controls.enabled = false;
        paintAt(e);
        e.preventDefault();
        return;
      }

      if (hits.length) {
        store.select(hits[0].object.userData.objectId);
      } else if (e.pointerType !== 'pen') {
        // tap empty → deselect on touch? keep selection
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!painting) return;
      paintAt(e);
    };
    const onPointerUp = () => {
      painting = false;
      controls.enabled = true;
    };

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    apiRef.current = { scene, camera, renderer, controls, objectGroup, rebuild };
    rebuild();

    // Expose thumbnail helper on element
    (el as unknown as { __capture?: () => Promise<Uint8Array | undefined> }).__capture = async () => {
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL('image/png');
      const res = await fetch(dataUrl);
      const buf = new Uint8Array(await res.arrayBuffer());
      return buf;
    };

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', resize);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, []);

  // Rebuild meshes when state changes
  useEffect(() => {
    apiRef.current?.rebuild();
  }, [objects, selectedId, tool, brushSize, overhangHighlight, overhangAngle, theme]);

  // Drag move when tool=move
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;
    const { renderer, camera, controls, objectGroup } = api;
    if (tool !== 'move' && tool !== 'rotate' && tool !== 'scale') return;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const hit = new THREE.Vector3();
    let dragging = false;
    let startBed: THREE.Vector3 | null = null;
    let startPos: [number, number, number] | null = null;

    const ndc = (e: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const down = (e: PointerEvent) => {
      const s = store.getSnapshot();
      if (!s.selectedId) return;
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      );
      if (!raycaster.intersectObjects(meshes as THREE.Object3D[], false).length) return;
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      dragging = true;
      controls.enabled = false;
      startBed = hit.clone();
      const o = s.objects.find((x) => x.id === s.selectedId)!;
      startPos = [...o.position] as [number, number, number];
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!dragging || !startBed || !startPos) return;
      const s = store.getSnapshot();
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const dx = hit.x - startBed.x;
      const dz = hit.z - startBed.z;
      if (tool === 'move') {
        store.updateObject(s.selectedId!, {
          position: [startPos[0] + dx, startPos[1] + dz, startPos[2]],
        });
      } else if (tool === 'rotate') {
        const yaw = dx * 0.02;
        const o = s.objects.find((x) => x.id === s.selectedId)!;
        store.updateObject(s.selectedId!, {
          rotation: [o.rotation[0], startPos[0] /* misuse */ + yaw, o.rotation[2]],
        });
        // Fix: store start rot
      } else if (tool === 'scale') {
        const factor = 1 + dx * 0.01;
        const o = s.objects.find((x) => x.id === s.selectedId)!;
        const f = Math.max(0.05, factor);
        store.updateObject(s.selectedId!, {
          scale: [o.scale[0] * (f / (o.scale[0] || 1)), o.scale[1] * (f / (o.scale[0] || 1)), o.scale[2] * (f / (o.scale[0] || 1))],
        });
      }
    };
    // Better rotate/scale handlers
    let startRotY = 0;
    let startScale = 1;
    const down2 = (e: PointerEvent) => {
      const s = store.getSnapshot();
      if (!s.selectedId) return;
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      const meshes = objectGroup.children.filter(
        (c) => (c as THREE.Mesh).isMesh && (c as THREE.Mesh).userData.objectId === s.selectedId
      );
      const hitMesh = raycaster.intersectObjects(meshes as THREE.Object3D[], false).length > 0;
      if (!hitMesh && tool === 'move') return;
      // rotate/scale: allow drag anywhere once an object is selected
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const o = s.objects.find((x) => x.id === s.selectedId);
      if (!o) return;
      dragging = true;
      controls.enabled = false;
      startBed = hit.clone();
      startPos = [...o.position] as [number, number, number];
      startRotY = o.rotation[1];
      startScale = o.scale[0];
      e.preventDefault();
    };
    const move2 = (e: PointerEvent) => {
      if (!dragging || !startBed || !startPos) return;
      const s = store.getSnapshot();
      ndc(e);
      raycaster.setFromCamera(pointer, camera);
      if (!raycaster.ray.intersectPlane(plane, hit)) return;
      const dx = hit.x - startBed.x;
      const dz = hit.z - startBed.z;
      if (tool === 'move') {
        store.updateObject(s.selectedId!, {
          position: [startPos[0] + dx, startPos[1] + dz, startPos[2]],
        });
      } else if (tool === 'rotate') {
        store.updateObject(s.selectedId!, {
          rotation: [
            store.getSnapshot().objects.find((x) => x.id === s.selectedId)!.rotation[0],
            startRotY + dx * 0.03,
            store.getSnapshot().objects.find((x) => x.id === s.selectedId)!.rotation[2],
          ],
        });
      } else if (tool === 'scale') {
        const f = Math.max(0.05, startScale * (1 + dx * 0.02));
        store.updateObject(s.selectedId!, { scale: [f, f, f] });
      }
    };
    const up = () => {
      dragging = false;
      controls.enabled = true;
      startBed = null;
    };

    renderer.domElement.addEventListener('pointerdown', down2);
    renderer.domElement.addEventListener('pointermove', move2);
    window.addEventListener('pointerup', up);
    return () => {
      renderer.domElement.removeEventListener('pointerdown', down2);
      renderer.domElement.removeEventListener('pointermove', move2);
      window.removeEventListener('pointerup', up);
      // silence unused
      void down;
      void move;
    };
  }, [tool]);

  useEffect(() => {
    if (!sceneRef.current) return;
    const dark = themeIsDark();
    sceneRef.current.background = new THREE.Color(dark ? 0x0f1218 : 0xf0f2f4);
    if (plateMatRef.current) {
      const old = plateMatRef.current.map;
      plateMatRef.current.map = makePeiTexture(dark);
      plateMatRef.current.color.set(0xffffff);
      plateMatRef.current.metalness = dark ? 0.18 : 0.02;
      plateMatRef.current.roughness = dark ? 0.85 : 0.95;
      plateMatRef.current.emissive.set(dark ? 0x000000 : 0xb0b0b0);
      plateMatRef.current.emissiveIntensity = dark ? 0 : 0.35;
      plateMatRef.current.needsUpdate = true;
      old?.dispose();
    }
    if (gridRef.current) {
      const mats = gridRef.current.material;
      const list = Array.isArray(mats) ? mats : [mats];
      if (list[0] && 'color' in list[0]) (list[0] as THREE.LineBasicMaterial).color.set(dark ? 0x6b5a3e : 0x52525b);
      if (list[1] && 'color' in list[1]) (list[1] as THREE.LineBasicMaterial).color.set(dark ? 0x3a3228 : 0x71717a);
    }
    if (volRef.current) {
      const mat = volRef.current.material as THREE.LineBasicMaterial;
      mat.color.set(dark ? 0x334155 : 0x4b5563);
    }
  }, [theme]);

  return <div className="plate-viewer" ref={mountRef} data-testid="plate-viewer" />;

}

export async function capturePlateThumbnail(): Promise<Uint8Array | undefined> {
  const el = document.querySelector('.plate-viewer') as
    | (HTMLDivElement & { __capture?: () => Promise<Uint8Array | undefined> })
    | null;
  return el?.__capture?.();
}
